import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  WalletCards,
} from 'lucide-react';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR');
};

const CashFlowProjectionPanel = ({ formatCurrency, refreshKey = 0, toast }) => {
  const [data, setData] = useState(null);
  const [horizonDays, setHorizonDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [expandedDays, setExpandedDays] = useState({});

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const loadProjection = async (days = horizonDays) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/billing/cash-flow/projection`, {
        params: { horizon_days: days },
        headers: headers(),
      });
      setData(response.data || null);
    } catch (error) {
      console.error('Erro ao carregar fluxo de caixa projetado:', error);
      toast?.error(error.response?.data?.detail || 'Não foi possível carregar o fluxo de caixa projetado.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjection(horizonDays);

  }, [horizonDays, refreshKey]);

  const timeline = Array.isArray(data?.timeline) ? data.timeline : [];
  const alerts = Array.isArray(data?.alerts) ? data.alerts : [];

  const summaryCards = useMemo(() => {
    if (!data) return [];
    return [
      {
        label: 'Saldo atual',
        value: data.current_balance,
        helper: 'Recebimentos menos pagamentos realizados',
        icon: WalletCards,
        className: Number(data.current_balance || 0) >= 0
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-red-50 text-red-700',
      },
      {
        label: 'Receber em 30 dias',
        value: data.next_30_days?.receivables,
        helper: 'Entradas previstas',
        icon: ArrowUpRight,
        className: 'bg-green-50 text-green-700',
      },
      {
        label: 'Pagar em 30 dias',
        value: data.next_30_days?.payables,
        helper: 'Saídas previstas',
        icon: ArrowDownRight,
        className: 'bg-red-50 text-red-700',
      },
      {
        label: 'Saldo projetado em 30 dias',
        value: data.next_30_days?.projected_balance,
        helper: 'Saldo atual somado ao fluxo previsto',
        icon: Number(data.next_30_days?.projected_balance || 0) >= 0 ? TrendingUp : TrendingDown,
        className: Number(data.next_30_days?.projected_balance || 0) >= 0
          ? 'bg-blue-50 text-blue-700'
          : 'bg-red-50 text-red-700',
      },
    ];
  }, [data]);

  if (loading && !data) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 text-gray-600">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Calculando fluxo de caixa projetado...
        </div>
      </div>
    );
  }

  if (!data) return null;

  const alertClass = (type) => {
    if (type === 'danger') return 'border-red-200 bg-red-50 text-red-900';
    if (type === 'warning') return 'border-amber-200 bg-amber-50 text-amber-900';
    return 'border-green-200 bg-green-50 text-green-900';
  };

  return (
    <div className="space-y-6 rounded-xl border border-gray-200 bg-white p-5 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Fluxo de Caixa Projetado</h2>
          <p className="mt-1 text-sm text-gray-600">
            Entradas e saídas consolidadas a partir das faturas, parcelas, receitas externas e despesas.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {[30, 60, 90, 180, 365].map((days) => (
            <button
              key={days}
              type="button"
              onClick={() => setHorizonDays(days)}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                horizonDays === days
                  ? 'bg-purple-600 text-white'
                  : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {days} dias
            </button>
          ))}
          <button
            type="button"
            onClick={() => loadProjection(horizonDays)}
            disabled={loading}
            className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="rounded-xl border border-gray-200 p-4">
              <div className={`inline-flex rounded-lg p-2 ${item.className}`}>
                <Icon className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm font-medium text-gray-600">{item.label}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(item.value)}</p>
              <p className="mt-1 text-xs text-gray-500">{item.helper}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          ['Hoje', data.today],
          ['Próximos 7 dias', data.next_7_days],
          ['Próximos 90 dias', data.next_90_days],
        ].map(([label, period]) => (
          <div key={label} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="font-semibold text-gray-900">{label}</p>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Receber</span>
                <span className="font-semibold text-green-700">{formatCurrency(period?.receivables)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pagar</span>
                <span className="font-semibold text-red-700">{formatCurrency(period?.payables)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-2">
                <span className="font-medium text-gray-700">Saldo projetado</span>
                <span className={`font-bold ${Number(period?.projected_balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                  {formatCurrency(period?.projected_balance)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {alerts.map((alert, index) => {
            const Icon = alert.type === 'positive' ? CheckCircle2 : AlertTriangle;
            return (
              <div key={`${alert.title}-${index}`} className={`flex gap-3 rounded-xl border p-4 ${alertClass(alert.type)}`}>
                <Icon className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">{alert.title}</p>
                  <p className="mt-1 text-sm">{alert.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div>
        <div className="mb-3 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">Calendário financeiro</h3>
        </div>

        {timeline.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
            Não há entradas ou saídas previstas no horizonte selecionado.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="max-h-[560px] overflow-y-auto">
              {timeline.map((day) => {
                const expanded = expandedDays[day.date] === true;
                return (
                  <div key={day.date} className="border-b border-gray-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setExpandedDays((prev) => ({ ...prev, [day.date]: !expanded }))}
                      className="grid w-full grid-cols-[auto_1fr] items-center gap-3 p-4 text-left hover:bg-gray-50 md:grid-cols-[auto_160px_1fr_1fr_1fr]"
                    >
                      {expanded
                        ? <ChevronDown className="h-4 w-4 text-gray-500" />
                        : <ChevronRight className="h-4 w-4 text-gray-500" />}
                      <span className="font-semibold text-gray-900">{formatDate(day.date)}</span>
                      <span className="hidden text-sm text-green-700 md:block">
                        Receber: <strong>{formatCurrency(day.receivables)}</strong>
                      </span>
                      <span className="hidden text-sm text-red-700 md:block">
                        Pagar: <strong>{formatCurrency(day.payables)}</strong>
                      </span>
                      <span className={`hidden text-sm md:block ${Number(day.projected_balance || 0) >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        Saldo: <strong>{formatCurrency(day.projected_balance)}</strong>
                      </span>
                    </button>

                    <div className="grid grid-cols-3 gap-2 px-4 pb-3 md:hidden">
                      <div className="rounded bg-green-50 p-2 text-xs text-green-800">
                        Receber<br /><strong>{formatCurrency(day.receivables)}</strong>
                      </div>
                      <div className="rounded bg-red-50 p-2 text-xs text-red-800">
                        Pagar<br /><strong>{formatCurrency(day.payables)}</strong>
                      </div>
                      <div className="rounded bg-blue-50 p-2 text-xs text-blue-800">
                        Saldo<br /><strong>{formatCurrency(day.projected_balance)}</strong>
                      </div>
                    </div>

                    {expanded && (
                      <div className="space-y-2 bg-gray-50 px-4 py-3">
                        {day.events.map((event) => (
                          <div key={`${event.source_type}-${event.id}`} className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  event.direction === 'in'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {event.direction === 'in' ? 'Entrada' : 'Saída'}
                                </span>
                                {event.status === 'overdue' && (
                                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                                    Vencido
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 font-medium text-gray-900">{event.title}</p>
                              <p className="text-sm text-gray-500">{event.description}</p>
                            </div>
                            <p className={`text-lg font-bold ${event.direction === 'in' ? 'text-green-700' : 'text-red-700'}`}>
                              {event.direction === 'in' ? '+' : '-'} {formatCurrency(event.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg bg-gray-50 px-4 py-3 text-xs text-gray-500">
        O saldo atual considera os recebimentos e pagamentos registrados no Financeiro. O saldo projetado adiciona os valores ainda abertos conforme seus vencimentos.
      </div>
    </div>
  );
};

export default CashFlowProjectionPanel;
